///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { GtthCustomerService } from '../../../services/customer/customer.service';
import { UserService } from '../../../users/service/user.service';
import { CustomerIdWithNameProjection, CustomerLocationDTO, UserDTO } from '../../../generated/shared-types.model';
import { GtthCustomerLocationService } from '../../../services/customer/customer-location.service';
import { Principal } from '../../../shared/auth/principal.service';
import { Store } from '@ngrx/store';
import { GtthLocalStorageService } from '../../../shared/auth/localStorage.service';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { SuiCustomerDTO } from '../../../generated/shared-sui-types.model';
import {
    SuiNumberControlComponent,
    SuiSelectControlComponent,
    SuiStringControlComponent,
    SuiToggleControlComponent,
    SuiTypeSelect
} from '@siklu/sui-lib/infrastructure';
import { finalize, from, Observable, of, tap } from 'rxjs';
import { createCustomer, editCustomer } from '../../store/customer-config.actions';
import { combineLatestWith, map } from 'rxjs/operators';
import { UserAuthorities } from '../../../services/user/user-authorities';
import { SuiLoadingDirective, SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { MultipleSelectComponent } from '../../../shared/sui-lib-customisations/sui-multiple-select/sui-winde-multiple-select.component';
import { JhiAlertErrorComponent } from '../../../shared/alert/alert-error.component';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';

@Component({
    selector: 'winde-customer-dialog',
    templateUrl: './customer-dialog.component.html',
    styleUrls: ['./customer-dialog.component.scss'],
    imports: [
        SuiLoadingDirective,
        SuiTranslatePipe,
        SuiStringControlComponent,
        SuiNumberControlComponent,
        SuiSelectControlComponent,
        SuiToggleControlComponent,
        SuiSvgIconComponent,
        MultipleSelectComponent,
        JhiAlertErrorComponent,
        ButtonsDirectivesModule,
        MatDialogModule
    ]
})
export class CustomerDialogComponent implements OnInit {
    public customer: SuiCustomerDTO;
    public locationControl: SuiTypeSelect<CustomerLocationDTO>;
    public parentCompanyField: SuiTypeSelect<CustomerIdWithNameProjection>;
    public loaderKey = LoaderIds.CUSTOMER_DIALOG_LOADER_ID;

    private currentUser: UserDTO;

    constructor(
        private readonly customerService: GtthCustomerService,
        private readonly customerLocationService: GtthCustomerLocationService,
        private readonly userService: UserService,
        private readonly principal: Principal,
        private readonly store: Store,
        private readonly localStorageService: GtthLocalStorageService,
        public dialogRef: MatDialogRef<CustomerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { payload: SuiCustomerDTO },
        public preloaderService: PreloaderService
    ) {}

    @HostListener('document:keydown.enter', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        event.preventDefault();
        this.save();
    }

    ngOnInit(): void {
        this.preloaderService.show(this.loaderKey);

        this.customer = this.data.payload;

        this.customer.name.isMandatory = true;
        this.customer.sikluCompany.applyModifiedStateStyle = false;

        this.customer.bomExportDaysTimeout.setValue(this.customer.bomExportDaysTimeout.value / 7);
        this.customer.designBomDaysTimeout.setValue(this.customer.designBomDaysTimeout.value / 7);

        of()
            .pipe(
                combineLatestWith(this.initPotentialParentsControl(), this.initSalesControl(), this.initCustomerLocationControl()),
                finalize(() => this.preloaderService.hide(this.loaderKey))
            )
            .subscribe();
    }

    save() {
        if (
            !this.customer.validateAndShowError() ||
            !this.parentCompanyField.validateAndShowError() ||
            !this.locationControl.validateAndShowError()
        ) {
            return;
        }

        this.preloaderService.show(this.loaderKey);

        const customerDTO = this.customer.toDto();

        customerDTO.name = customerDTO.name.trim();

        if (customerDTO.sikluCompany || !this.parentCompanyField?.value?.id) {
            customerDTO.parentCompany = undefined;
        } else {
            customerDTO.parentCompany = { id: this.parentCompanyField.value.id, name: this.parentCompanyField.value.name };
        }

        customerDTO.bomExportDaysTimeout = customerDTO.bomExportDaysTimeout * 7;
        customerDTO.designBomDaysTimeout = customerDTO.designBomDaysTimeout * 7;

        customerDTO.location = this.locationControl.value;

        // sales user can see only companies assigned to.
        // therefore, is current user is a sale it must be assigned to company he/she reviewing
        if (this.currentUser.authorities.includes(UserAuthorities.SIKLU_SALES)) {
            customerDTO.sales.push(this.currentUser);
        }

        const request = customerDTO.id !== undefined ? this.customerService.update(customerDTO) : this.customerService.create(customerDTO);

        request
            .pipe(
                tap((response) => {
                    this.localStorageService.setSelectedCustomerId(response.id);

                    if (customerDTO.id !== undefined) {
                        this.store.dispatch(editCustomer({ updatedCustomer: response }));
                    } else {
                        this.store.dispatch(createCustomer({ newCustomer: response }));
                    }
                    this.dialogRef.close(response);
                }),
                finalize(() => {
                    this.preloaderService.hide(this.loaderKey);
                })
            )
            .subscribe();
    }

    public getChildCompanyNames() {
        return this.customer.childCompanies.map((child) => child.name.value).join(', ');
    }

    private initPotentialParentsControl(): Observable<CustomerIdWithNameProjection[]> {
        return this.customerService.getLightListForParent().pipe(
            tap((parentCompanies: CustomerIdWithNameProjection[]) => {
                const parentCompaniesExcludeCurrent = parentCompanies.filter(
                    (potentialParent) => potentialParent.id !== this.customer.id.value
                );

                parentCompaniesExcludeCurrent.unshift({ id: undefined, name: 'none' });

                const selectedOption = parentCompaniesExcludeCurrent.find(
                    (company) => company.id === this.customer?.parentCompany?.id?.value
                );
                this.parentCompanyField = new SuiTypeSelect<CustomerIdWithNameProjection>(
                    selectedOption,
                    parentCompaniesExcludeCurrent,
                    'name'
                );
                this.parentCompanyField.isMandatory = false;
                this.parentCompanyField.isFilterable = true;
            })
        );
    }

    private initSalesControl(): Observable<unknown> {
        return this.userService.loadAllSalesWithoutPaging().pipe(
            combineLatestWith(from(this.principal.identity())),
            map(([salesUsers, currentUser]) => {
                this.currentUser = currentUser;
                const allSales = salesUsers.filter((user) => user.id !== currentUser.id);

                this.customer.sales.setAvailableItems(allSales);
                this.customer.sales.isMandatory = !this.currentUser.authorities.includes(UserAuthorities.SIKLU_SALES);
            })
        );
    }

    private initCustomerLocationControl(): Observable<unknown> {
        return this.customerLocationService.getAvailableLocations().pipe(
            tap((availableLocations: CustomerLocationDTO[]) => {
                const selectedLocation = availableLocations.find((location) => location.id === this.customer?.location?.id);
                this.locationControl = new SuiTypeSelect<CustomerLocationDTO>(selectedLocation, availableLocations, 'name');
            })
        );
    }
}
